import React, { useState } from 'react';
import axios from 'axios';
import './otpsignup.scss';
import { Autocomplete } from '@react-google-maps/api';

const SignUp = ({ onComplete, handleLogin }) => {

    // State for all SignUp form fields
    const [signUpState, setSignUpState] = useState({
        name: "",
        surname: "",
        addressPrimary: "",
        email: "",
        password: "",
        confirmPassword: "",
        contactNumber: "",
        employeeNumber: "",
        termsAndConditions: false,
        showError: false,
        errorMessage: "",
        token: "",
        passwordShow: false,
        confirmPasswordShow: false,
        requireEmployeeNumber: false
    });
    const [signUpIsLoading, setSignUpIsLoading] = useState(false);
    const [autocomplete, setAutocomplete] = useState(null);
    const onValueChanged = (type, value) => {
        setSignUpState((prevState) => ({
            ...prevState,
            [type]: value
        }));
    }

    const onValidate = async () => {
        try {
            const { name, surname, addressPrimary, email, password, confirmPassword, termsAndConditions, contactNumber } = signUpState;
            const trimmedContactNumber = contactNumber?.trim()?.replace(/\s/g, '');
            const trimmedLowerEmail = email?.trim()?.toLowerCase()?.replace(/\s/g, '');

            if (name === "" || surname === "" || email === "" || contactNumber === "") {
            throw new Error('Please ensure your contact details are correct.');
            }
            if (trimmedContactNumber?.length !== 10) {
            throw new Error('Your contact number is incorrect, it should be 10 digits, eg 0762338289.');
            }
            if (addressPrimary === "") throw new Error('Please enter a valid home address.');
            if (password === "") throw new Error('Please enter a valid password.');
            if (password.length < 6) throw new Error('Your password must be at least 6 characters long.');
            if (confirmPassword === "") throw new Error('Please confirm your password.');
            if (confirmPassword !== password) throw new Error('Passwords do not match.');
            if (termsAndConditions === false) throw new Error('Please read and accept our Terms & Conditions.');

            setSignUpIsLoading(true);

            // Send OTP request to backend
            await axios('/otp_signup/send_otp', {
                method: 'POST',
                data: { email: trimmedLowerEmail, contact_number: trimmedContactNumber },
                headers: { Accept: 'application/json' },
            });

            let requireEmployeeNumber = false;

            try {
                const { data } = await axios.get('/otp_signup/employee_requirement', {
                    params: { email: trimmedLowerEmail },
                    headers: { Accept: 'application/json' },
                });
                requireEmployeeNumber = !!data?.require_employee_id;
            } catch (err) {
                console.log(err)
            }

            if (requireEmployeeNumber && !signUpState.employeeNumber?.trim()) {
                setSignUpIsLoading(false);
                setSignUpState(prev => ({
                    ...prev,
                    requireEmployeeNumber: true,
                    showError: false,
                    errorMessage: 'Your company requires an employee number.'
                }));
                setTimeout(() => {
                    setSignUpState(prev => ({ ...prev, showError: false, errorMessage: "" }));
                }, 5000);
                return;
            }

            setSignUpIsLoading(false);

            if (onComplete) {
                onComplete({
                    ...signUpState,
                    token: "",
                    email: trimmedLowerEmail
                });
            }
        } catch (e) {
            setSignUpIsLoading(false)
            
            setSignUpState(prevState => ({
                ...prevState,
                showError: true,
                errorMessage: e.response?.data?.message ? e.response?.data?.message : e.message
            }));
            setTimeout(() => {
                setSignUpState(prevState => ({
                    ...prevState,
                    showError: false,
                    errorMessage: ""
                }));
            }, 5000)
        }
    }

    const onPlaceChanged = () => {
        if (autocomplete !== null) {
            const formated_address = autocomplete.getPlace()?.formatted_address;
            const address = (formated_address == null) ? "" : formated_address;
            onValueChanged("addressPrimary", address)
        }
        else {
            console.log('Autocomplete is not loaded yet!')
        }
    }

    return (
        <div>
            {/* Contact Details Inputs */}
            <div className="w-full mt-3">
                <label className="inline-block text-sm text-gray-500">Email Address</label>
                <input
                    className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                    type="text"
                    id="signup_email_address"
                    onChange={(e) => onValueChanged("email", e.target.value)}
                    value={signUpState.email}
                />
            </div>

            {/* Name Inputs */}
            <div className='grid md:grid-cols-2 md:gap-4 mt-3'>
                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Name</label>
                    <input
                        className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="text"
                        id="signup_name"
                        onChange={(e) => onValueChanged("name", e.target.value)}
                        value={signUpState.name}
                    />
                </div>
                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Surname</label>
                    <input
                        className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="text"
                        id="signup_surname"
                        onChange={(e) => onValueChanged("surname", e.target.value)}
                        value={signUpState.surname}
                    />
                </div>
            </div>

            <div className='grid md:grid-cols-2 md:gap-4 mt-3'>
                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Contact Number</label>
                    <div>
                        <input
                            className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type="text"
                            id="signup_contact_number"
                            onChange={(e) => onValueChanged("contactNumber", e.target.value)}
                            value={signUpState.contactNumber}
                            maxLength={10}
                            onKeyPress={(e) => {
                                const pattern = /[0-9]/;
                                const inputChar = String.fromCharCode(e.charCode);
                                if (!pattern.test(inputChar)) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Employee Number</label>
                    <input
                        className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="text"
                        id="signup_employee_number"
                        onChange={(e) => onValueChanged("employeeNumber", e.target.value)}
                        value={signUpState.employeeNumber}
                    />
                    {signUpState.requireEmployeeNumber && (
                        <span className="text-xs text-red-500">Required by your company</span>
                    )}
                </div>
            </div>

            {/* Address Input */}
            <div className="w-full mt-3">
                <label className="inline-block text-sm text-gray-500">Home Address</label>
                <Autocomplete
                    onLoad={(autocomplete) => {
                        setAutocomplete(autocomplete);
                    }}
                    restrictions={{
                        country: ['za', 'eg']
                    }}
                    onPlaceChanged={() => onPlaceChanged()}
                >
                    <input
                        className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="text"
                        id="signup_primary_address"
                        defaultValue={signUpState.addressPrimary}
                    />
                </Autocomplete>
            </div>

            {/* Password Input */}
            <div className='grid md:grid-cols-2 md:gap-4 mt-3'>
                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Password</label>
                    <div className="relative">
                        <input
                            className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type={signUpState.passwordShow ? "text" : "password"}
                            id="signup_password"
                            onChange={(e) => onValueChanged("password", e.target.value)}
                            value={signUpState.password}
                        />
                        <button
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-purple-500 hover:text-purple-800 focus:outline-none"
                            onClick={() => onValueChanged("passwordShow", !signUpState.passwordShow)}
                        >
                            {signUpState.passwordShow ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                        </button>
                    </div>
                    <span className="text-xs text-gray-500">Must be at least 6 characters</span>
                </div>
                <div className="w-full">
                    <label className="inline-block text-sm text-gray-500">Confirm Password</label>
                    <div className='relative'>
                        <input
                            className="block appearance-none w-full bg-white border border-gray-400 text-gray-700 py-3 px-4 pr-12 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            type={signUpState.confirmPasswordShow ? "text" : "password"}
                            id="signup_password_confirm"
                            onChange={(e) => onValueChanged("confirmPassword", e.target.value)}
                            value={signUpState.confirmPassword}
                        />
                        <button
                            className='absolute inset-y-0 right-0 flex items-center pr-3 text-purple-500 hover:text-purple-800 focus:outline-none'
                            onClick={() => onValueChanged("confirmPasswordShow", !signUpState.confirmPasswordShow)}
                        >
                            {signUpState.confirmPasswordShow ? <i className='fas fa-eye-slash'></i> : <i className='fas fa-eye'></i>}
                        </button>
                    </div>
                    <span className='text-xs text-gray-500'>Must be at least 6 characters</span>
                </div>
            </div>

            <div className='flex mt-3'>

                {/* Terms and Conditions */}
                <div className='w-3/4 m-2'>
                    <input
                        type="checkbox"
                        className='w-auto'
                        value={signUpState.termsAndConditions}
                        checked={signUpState.termsAndConditions}
                        name="user[terms_and_conditions]"
                        id="user_terms_and_conditions"
                        onChange={(e) => onValueChanged("termsAndConditions", !signUpState.termsAndConditions)}
                    />
                    <p className='inline-block text-sm text-gray-500 ml-2 mt-1'>I understand and accept the <a target="_blank" rel="noopener noreferrer" className="text-purple-500 underline" href="/terms_and_conditions/index_customer">Terms &amp; Conditions</a></p>
                </div>

                {/* Submit */}
                <div className='w-1/4 mt-2'>
                    <button
                        className='w-full cursor-pointer text-sm px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-800'
                        onClick={() => onValidate("validate")}
                    >
                        Validate
                    </button>
                </div>
            </div>

            {/* Sign Up Error */}
            {signUpState.showError && (
                <h3 className="text-red-500 text-center mt-2">
                    <i className="fas fa-exclamation-circle"></i> {signUpState.errorMessage}
                </h3>
            )}

            {/* Log In */}
            <p className="mt-4 text-sm text-center text-gray-500 cursor-pointer">
                Already have an account?{' '}
                <span
                    className="text-purple-500 cursor-pointer underline"
                    onClick={() => {handleLogin()}}
                >
                    Log in!
                </span>
            </p>
        </div>
    )
}

export default SignUp;
